import { LSelect } from "./ui/LSelect";
import { useEffect, useState, useCallback } from "react";
import { loyaltyService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { Gift, Loader2, Plus, X } from "lucide-react";

const BAL_TYPES = ["points", "cashback", "voucher", "reward"];
const TXN_TYPES = ["earn", "redeem", "adjust", "expire"];

export default function LoyaltyTab({ customerId, canEdit }) {
  const { format } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ balanceType: "points", type: "earn", amount: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await loyaltyService.customerSummary(customerId)); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await loyaltyService.createTransaction(customerId, { ...form, amount: Number(form.amount), reason: form.reason || undefined });
      toast.success("Transaction recorded");
      setOpen(false); setForm({ balanceType: "points", type: "earn", amount: "", reason: "" }); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const w = data?.wallet;
  const txns = data?.transactions || [];

  return (
    <div data-testid="detail-loyalty">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground flex items-center gap-2"><Gift className="h-4.5 w-4.5 text-primary" /> Loyalty</h3>
        {canEdit && <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="loyalty-tab-add-txn"><Plus className="h-4 w-4" /> New Transaction</button>}
      </div>

      {!w ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="loyalty-tab-empty">
          No loyalty wallet yet for this customer.{canEdit ? " Record a transaction to create one." : ""}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {[["Points", (w.pointsBalance || 0).toLocaleString()], ["Cashback", format(w.cashbackBalance || 0)],
              ["Vouchers", (w.voucherBalance || 0).toLocaleString()], ["Tier", w.tierName || "—"]].map(([l, val]) => (
              <div key={l} className="bg-card border border-border rounded-xl p-4"><p className="text-xs text-muted-foreground">{l}</p><p className="text-lg font-bold text-foreground mt-1">{val}</p></div>
            ))}
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm font-semibold text-foreground mb-3">Recent Activity</p>
            {txns.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No activity</p> : (
              <div className="space-y-2">
                {txns.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                    <span className="capitalize text-foreground">{t.type} <span className="text-muted-foreground">· {t.reason || t.balanceType}</span></span>
                    <span className={`font-semibold ${t.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{t.delta >= 0 ? "+" : ""}{t.balanceType === "cashback" ? format(t.delta) : `${t.delta} ${t.balanceType}`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="loyalty-tab-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="font-semibold text-foreground">New Loyalty Transaction</h3>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-muted-foreground hover:text-foreground" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-foreground mb-1">Balance</label>
                  <LSelect value={form.balanceType} onChange={(e) => setForm({ ...form, balanceType: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">{BAL_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}</LSelect></div>
                <div><label className="block text-sm font-medium text-foreground mb-1">Type</label>
                  <LSelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">{TXN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</LSelect></div>
              </div>
              <div><label className="block text-sm font-medium text-foreground mb-1">Amount</label>
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="loyalty-tab-amount" /></div>
              <div><label className="block text-sm font-medium text-foreground mb-1">Reason</label>
                <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" /></div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button>
              <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="loyalty-tab-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
